// src/features/auth/components/TwoFactorOnboardingModal.tsx
import React, { useState } from 'react';
import { XMarkIcon, ShieldCheckIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

interface TwoFactorOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSetup: () => void;
  onSkip: () => void;
  onDontShowAgain: () => void;
}

const TwoFactorOnboardingModal: React.FC<TwoFactorOnboardingModalProps> = ({
  isOpen,
  onClose,
  onSetup,
  onSkip,
  onDontShowAgain
}) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSetup = async () => {
    setLoading(true);
    await onSetup();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Background overlay with blur effect */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />

      {/* Modal panel */}
      <div className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 text-left overflow-hidden shadow-xl transform transition-all max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <ShieldCheckIcon className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-lg font-medium text-theme-text dark:text-dark-theme-text">
                Secure Your Account
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <DevicePhoneMobileIcon className="h-6 w-6 text-primary-light dark:text-primary-dark mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-theme-text dark:text-dark-theme-text mb-1">
                  Enable Two-Factor Authentication
                </h4>
                <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                  Add an extra layer of security to your account. Use your phone to verify login attempts and protect against unauthorized access.
                </p>
              </div>
            </div>

            <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-md p-3">
              <div className="flex items-center space-x-2 text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
                <ShieldCheckIcon className="h-4 w-4 text-green-500" />
                <span>Recommended for enhanced security</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 space-y-3">
            <button
              onClick={handleSetup}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
            >
              {loading ? 'Setting up...' : 'Set Up 2FA Now'}
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={onSkip}
                className="flex-1 py-2 px-4 border border-theme-border dark:border-dark-theme-border rounded-md text-sm font-medium text-theme-text dark:text-dark-theme-text hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-colors duration-150"
              >
                Maybe Later
              </button>
              
              <button
                onClick={onDontShowAgain}
                className="flex-1 py-2 px-4 text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none transition-colors duration-150"
              >
                Don't Show Again (30 Days)
              </button>
            </div>
          </div>
        </div>
    </div>
  );
};

export default TwoFactorOnboardingModal;
